import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import GoogleCallback from "@/pages/GoogleCallback";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminVendedores from "@/pages/admin/AdminVendedores";
import AdminProductos from "@/pages/admin/AdminProductos";
import AdminInventario from "@/pages/admin/AdminInventario";
import AdminVentas from "@/pages/admin/AdminVentas";
import AdminReportes from "@/pages/admin/AdminReportes";
import AdminCierres from "@/pages/admin/AdminCierres";
import AdminRequisiciones from "@/pages/admin/AdminRequisiciones";
import AdminGastos from "@/pages/admin/AdminGastos";
import AdminPedidos from "@/pages/admin/AdminPedidos";
import AdminConfigLanding from "@/pages/admin/AdminConfigLanding";
import AdminNomina from "@/pages/admin/AdminNomina";
import AdminSolicitudes from "@/pages/admin/AdminSolicitudes";
import VendedorDashboard from "@/pages/vendedor/VendedorDashboard";
import VendedorPerfil from "@/pages/vendedor/VendedorPerfil";
import VendedorRequisiciones from "@/pages/vendedor/VendedorRequisiciones";
import VendedorVenta from "@/pages/vendedor/VendedorVenta";
import VendedorInventario from "@/pages/vendedor/VendedorInventario";
import VendedorCierre from "@/pages/vendedor/VendedorCierre";
import VendedorHistorial from "@/pages/vendedor/VendedorHistorial";
import VendedorPedidos from "@/pages/vendedor/VendedorPedidos";
import VendedorCatalogOrders from "@/pages/vendedor/VendedorCatalogOrders";
import VendedorGastos from "@/pages/vendedor/VendedorGastos";
import VendedorBienvenida from "@/pages/vendedor/VendedorBienvenida";
import RegistroVendedor from "@/pages/public/RegistroVendedor";
import AdminPedidosTerceros from "@/pages/admin/AdminPedidosTerceros";
import AdminPedidosOnline from "@/pages/admin/AdminPedidosOnline";

// New Onboarding Flow
import RegistroLayout from "@/pages/public/registro/RegistroLayout";
import RegistroLanding from "@/pages/public/registro/RegistroLanding";
import RegistroAcceso from "@/pages/public/registro/RegistroAcceso";
import RegistroTipo from "@/pages/public/registro/RegistroTipo";
import RegistroDatos from "@/pages/public/registro/RegistroDatos";
import RegistroEspera from "@/pages/public/registro/RegistroEspera";

import CatalogPage from "@/pages/CatalogPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import AdminCatalogoUnificado from "@/pages/admin/AdminCatalogoUnificado";
import AdminDespachos from "@/pages/admin/AdminDespachos";
import AdminDomiciliarios from "@/pages/admin/AdminDomiciliarios";
import AdminEmbajadores from "@/pages/admin/AdminEmbajadores";
import AdminZonasEnvio from "@/pages/admin/AdminZonasEnvio";
import AdminCapacidades from "@/pages/admin/AdminCapacidades";
import AdminMayoristas from "@/pages/admin/AdminMayoristas";
import AdminIntegraciones from "@/pages/admin/AdminIntegraciones";
import AdminIncentivos from "@/pages/admin/AdminIncentivos";
import VendedorDesempeno from "@/pages/vendedor/VendedorDesempeno";
import AdminClientes from "@/pages/admin/AdminClientes";
import RegistroNegocioB2B from "@/pages/public/RegistroNegocioB2B";
import VendedorNegocios from "@/pages/vendedor/VendedorNegocios";
import AdminNegociosB2B from "@/pages/admin/AdminNegociosB2B";
import AdminAperturas from "@/pages/admin/AdminAperturas";
import ClubAbuela from "@/pages/club/ClubAbuela";
import VendedorClub from "@/pages/vendedor/VendedorClub";
import AdminClub from "@/pages/admin/AdminClub";
import AdminClubMarketing from "@/pages/admin/AdminClubMarketing";

// Mayorista pages
import MayoristaLayout from "@/layouts/MayoristaLayout";
import MayoristaDashboard from "@/pages/mayorista/MayoristaDashboard";
import MayoristaComprar from "@/pages/mayorista/MayoristaComprar";
import MayoristaPedidos from "@/pages/mayorista/MayoristaPedidos";
import MayoristaCuenta from "@/pages/mayorista/MayoristaCuenta";
import MayoristaClub from "@/pages/mayorista/MayoristaClub";

// Casa Matriz pages
import CasaMatrizDashboard from "@/pages/casamatriz/CasaMatrizDashboard";
import CasaMatrizPedidos from "@/pages/casamatriz/CasaMatrizPedidos";
import CasaMatrizDespachos from "@/pages/casamatriz/CasaMatrizDespachos";

// Layouts
import AdminLayout from "@/layouts/AdminLayout";
import VendedorLayout from "@/layouts/VendedorLayout";
import CasaMatrizLayout from "@/layouts/CasaMatrizLayout";
import DomiciliarioLayout from "@/layouts/DomiciliarioLayout";

// Delivery pages
import DeliveryDashboard from "@/pages/delivery/DeliveryDashboard";
import DeliveryHistory from "@/pages/delivery/DeliveryHistory";
import DeliveryProfile from "@/pages/delivery/DeliveryProfile";

// Embajador pages
import EmbajadorRegistro from "@/pages/embajador/EmbajadorRegistro";
import EmbajadorLogin from "@/pages/embajador/EmbajadorLogin";
import EmbajadorDashboard from "@/pages/embajador/EmbajadorDashboard";
import EmbajadorContenido from "@/pages/embajador/EmbajadorContenido";
import EmbajadorPedidos from "@/pages/embajador/EmbajadorPedidos";
import EmbajadorComisiones from "@/pages/embajador/EmbajadorComisiones";
import EmbajadorPerfil from "@/pages/embajador/EmbajadorPerfil";

// Auth context
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-torta-cream">
        <div className="text-center">
          <div className="w-16 h-16 bg-torta-brown rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-2xl">🥧</span>
          </div>
          <p className="text-torta-dark font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.rol)) {
    // Redirect based on role
    const roleRoutes = {
      admin: "/admin",
      vendedor: "/vendedor",
      casa_matriz: "/casa-matriz",
      domiciliario: "/delivery/dashboard",
      distribuidor: "/mayorista"
    };
    return <Navigate to={roleRoutes[user.rol] || "/"} replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <GoogleCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Old Registration (keeping for backwards compatibility) */}
      <Route path="/registro-vendedor-legacy" element={<RegistroVendedor />} />
      
      {/* New Onboarding Flow */}
      <Route path="/registro-vendedor" element={<RegistroLayout />}>
        <Route index element={<RegistroLanding />} />
        <Route path="acceso" element={<RegistroAcceso />} />
        <Route path="tipo" element={<RegistroTipo />} />
        <Route path="datos" element={<RegistroDatos />} />
        <Route path="espera" element={<RegistroEspera />} />
      </Route>
      
      <Route path="/catalogo/:sellerCode" element={<CatalogPage />} />
      <Route path="/s/:affiliateCode" element={<CatalogPage />} />
      <Route path="/club" element={<ClubAbuela />} />
      <Route path="/negocios" element={<RegistroNegocioB2B />} />
      <Route path="/pedido/:orderNumber" element={<OrderTrackingPage />} />
      <Route path="/pedido" element={<OrderTrackingPage />} />
      
      {/* Embajador Public Routes */}
      <Route path="/embajador/registro" element={<EmbajadorRegistro />} />
      <Route path="/embajador/login" element={<EmbajadorLogin />} />
      
      {/* Embajador Protected Routes (handled internally) */}
      <Route path="/embajador/dashboard" element={<EmbajadorDashboard />} />
      <Route path="/embajador/contenido" element={<EmbajadorContenido />} />
      <Route path="/embajador/pedidos" element={<EmbajadorPedidos />} />
      <Route path="/embajador/comisiones" element={<EmbajadorComisiones />} />
      <Route path="/embajador/perfil" element={<EmbajadorPerfil />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin", "supervisor"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="vendedores" element={<AdminVendedores />} />
        <Route path="productos" element={<AdminProductos />} />
        <Route path="inventario" element={<AdminInventario />} />
        <Route path="ventas" element={<AdminVentas />} />
        <Route path="cierres" element={<AdminCierres />} />
        <Route path="reportes" element={<AdminReportes />} />
        <Route path="comisiones" element={<Navigate to="/admin/incentivos" replace />} />
        <Route path="incentivos" element={<AdminIncentivos />} />
        <Route path="clientes" element={<AdminClientes />} />
        <Route path="aperturas" element={<AdminAperturas />} />
        <Route path="club" element={<AdminClub />} />
        <Route path="club-whatsapp" element={<AdminClubMarketing />} />
        <Route path="nomina" element={<AdminNomina />} />
        <Route path="requisiciones" element={<AdminRequisiciones />} />
        <Route path="gastos" element={<AdminGastos />} />
        <Route path="pedidos" element={<AdminPedidos />} />
        <Route path="config-landing" element={<AdminConfigLanding />} />
        <Route path="solicitudes" element={<AdminSolicitudes />} />
        <Route path="pedidos-terceros" element={<AdminPedidosTerceros />} />
        <Route path="pedidos-online" element={<AdminPedidosOnline />} />
        <Route path="catalogo" element={<AdminCatalogoUnificado />} />
        <Route path="despachos" element={<AdminDespachos />} />
        <Route path="domiciliarios" element={<AdminDomiciliarios />} />
        <Route path="embajadores" element={<AdminEmbajadores />} />
        <Route path="zonas-envio" element={<AdminZonasEnvio />} />
        <Route path="capacidades" element={<AdminCapacidades />} />
        <Route path="mayoristas" element={<AdminMayoristas />} />
        <Route path="negocios-b2b" element={<AdminNegociosB2B />} />
        <Route path="integraciones" element={<AdminIntegraciones />} />
        <Route path="perfil" element={<VendedorPerfil />} />
      </Route>

      {/* Casa Matriz Routes */}
      <Route
        path="/casa-matriz"
        element={
          <ProtectedRoute allowedRoles={["casa_matriz"]}>
            <CasaMatrizLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CasaMatrizDashboard />} />
        <Route path="pedidos" element={<CasaMatrizPedidos />} />
        <Route path="despachos" element={<CasaMatrizDespachos />} />
      </Route>

      {/* Vendedor Routes */}
      <Route
        path="/vendedor"
        element={
          <ProtectedRoute allowedRoles={["vendedor"]}>
            <VendedorLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<VendedorDashboard />} />
        <Route path="venta" element={<VendedorVenta />} />
        <Route path="inventario" element={<VendedorInventario />} />
        <Route path="historial" element={<VendedorHistorial />} />
        <Route path="cierre" element={<VendedorCierre />} />
        <Route path="perfil" element={<VendedorPerfil />} />
        <Route path="requisiciones" element={<VendedorRequisiciones />} />
        <Route path="nomina" element={<Navigate to="/vendedor/desempeno" replace />} />
        <Route path="desempeno" element={<VendedorDesempeno />} />
        <Route path="club" element={<VendedorClub />} />
        <Route path="negocios" element={<VendedorNegocios />} />
        <Route path="pedidos" element={<VendedorPedidos />} />
        <Route path="catalogo-pedidos" element={<VendedorCatalogOrders />} />
        <Route path="gastos" element={<VendedorGastos />} />
        <Route path="bienvenida" element={<VendedorBienvenida />} />
      </Route>

      {/* Mayorista Routes */}
      <Route
        path="/mayorista"
        element={
          <ProtectedRoute allowedRoles={["distribuidor"]}>
            <MayoristaLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<MayoristaDashboard />} />
        <Route path="comprar" element={<MayoristaComprar />} />
        <Route path="pedidos" element={<MayoristaPedidos />} />
        <Route path="cuenta" element={<MayoristaCuenta />} />
        <Route path="club" element={<MayoristaClub />} />
      </Route>

      {/* Domiciliario/Delivery Routes */}
      <Route
        path="/delivery"
        element={
          <ProtectedRoute allowedRoles={["domiciliario", "vendedor"]}>
            <DomiciliarioLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/delivery/dashboard" replace />} />
        <Route path="dashboard" element={<DeliveryDashboard />} />
        <Route path="historial" element={<DeliveryHistory />} />
        <Route path="perfil" element={<DeliveryProfile />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
